/**
 * @fileoverview WebAuthn Service - Biometric authentication and hardware security key integration
 *
 * This service provides WebAuthn (Web Authentication API) credential management for
 * biometric authentication and hardware security keys. It enables users to unlock
 * their encryption keys using Touch ID, Face ID, Windows Hello, or hardware security
 * keys (like YubiKey) instead of passwords.
 *
 * **Supported Authenticators:**
 * - Platform Authenticators: Touch ID (Mac/iOS), Face ID (iOS), Windows Hello
 * - Cross-Platform Authenticators: YubiKey, Google Titan, other FIDO2 devices
 *
 * **Architecture:**
 * - Browser: SimpleWebAuthn client library handles browser API interactions
 * - Edge Functions: Supabase Edge Functions handle server-side verification
 * - Database: Credentials stored in webauthn_credentials table
 * - Key Wrapping: Master encryption key wrapped with WebAuthn credential
 *
 * **Security Features:**
 * - Hardware-backed key storage (Secure Enclave, TPM)
 * - User presence verification (biometric or PIN)
 * - Phishing-resistant authentication (origin binding)
 * - Credential isolation per device
 * - Key backup and recovery support
 *
 * **Usage Flow:**
 * 1. Check platform support → isPlatformAuthenticatorAvailable()
 * 2. Register credential → registerWebAuthnCredential()
 * 3. Wrap master key → (handled internally)
 * 4. Store credential → (handled internally via edge function)
 * 5. Later: Authenticate → authenticateWithWebAuthn()
 * 6. Unwrap master key → (handled internally)
 *
 * @module services/WebAuthnService
 * @requires @simplewebauthn/browser
 * @requires @/integrations/supabase/client
 * @requires @/lib/logger
 * @see Technical Plan: docs/technical-specs/webauthn-encryption-unlock-plan.md
 * @see WebAuthn Guide: https://webauthn.guide/
 */

import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { getCachedMasterKeyMaterial } from '@xanectech/encryption';
import { supabase } from '@xanectech/supabase-client';
import { createLogger, isRecord } from '@xanectech/lib';

const logger = createLogger('WebAuthnService');

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface WebAuthnCredential {
  id: string;
  credentialId: string;
  deviceName: string;
  deviceType: 'platform' | 'cross-platform';
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  backupEligible: boolean;
  backupState: boolean;
}

export interface WebAuthnRegistrationOptions {
  userId: string;
  deviceName: string;
  deviceType?: 'platform' | 'cross-platform';
}

export interface AuthenticationOptions {
  userId: string;
  credentialId?: string; // Optional: authenticate with specific credential
}

export interface WrappedKey {
  encryptedMasterKey: string;
  keyWrapAlgorithm: string;
  keyWrapIv: string;
}

export interface WebAuthnRegistrationResult {
  credential: WebAuthnCredential;
  wrappedKey: WrappedKey;
}

export interface AuthenticationResult {
  success: boolean;
  wrappedKey?: WrappedKey;
  credentialId?: string;
  error?: string;
}

interface InvokeResponseEnvelope {
  data?: unknown;
  error?: unknown;
}

interface RegistrationOptionsResponse {
  options: PublicKeyCredentialCreationOptionsJSON;
}

interface AuthenticationOptionsResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
}

interface RegistrationVerificationResponse {
  credential: WebAuthnCredential;
  wrappedKey: WrappedKey;
}

interface AuthenticationVerificationResponse {
  wrappedKey: WrappedKey;
  credentialId?: string;
}

interface WrappedKeyResponse {
  wrappedKey: WrappedKey;
}

interface PrivateKeyResponse {
  privateKeyPem: string;
}

const getInvokeEnvelope = (value: unknown): InvokeResponseEnvelope => {
  if (!isRecord(value)) {
    return {};
  }

  return {
    data: 'data' in value ? value.data : undefined,
    error: 'error' in value ? value.error : undefined,
  };
};

const getUnknownMessage = (value: unknown, fallback: string): string => {
  if (value instanceof Error) {
    return value.message;
  }

  if (isRecord(value) && typeof value.message === 'string') {
    return value.message;
  }

  if (typeof value === 'string') {
    return value;
  }

  return fallback;
};

const getUnknownName = (value: unknown): string | undefined => {
  if (isRecord(value) && typeof value.name === 'string') {
    return value.name;
  }

  if (value instanceof Error) {
    return value.name;
  }

  return undefined;
};

const isWrappedKey = (value: unknown): value is WrappedKey =>
  isRecord(value) &&
  typeof value.encryptedMasterKey === 'string' &&
  typeof value.keyWrapAlgorithm === 'string' &&
  typeof value.keyWrapIv === 'string';

const hasRegistrationOptions = (
  value: unknown,
): value is RegistrationOptionsResponse =>
  isRecord(value) && isRecord(value.options);

const hasAuthenticationOptions = (
  value: unknown,
): value is AuthenticationOptionsResponse =>
  isRecord(value) && isRecord(value.options);

const hasRegistrationVerification = (
  value: unknown,
): value is RegistrationVerificationResponse =>
  isRecord(value) &&
  isRecord(value.credential) &&
  isWrappedKey(value.wrappedKey);

const hasAuthenticationVerification = (
  value: unknown,
): value is AuthenticationVerificationResponse =>
  isRecord(value) && isWrappedKey(value.wrappedKey);

const hasWrappedKeyResponse = (value: unknown): value is WrappedKeyResponse =>
  isRecord(value) && isWrappedKey(value.wrappedKey);

const hasPrivateKeyResponse = (value: unknown): value is PrivateKeyResponse =>
  isRecord(value) && typeof value.privateKeyPem === 'string';

// ============================================================================
// Feature Detection
// ============================================================================

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.PublicKeyCredential !== undefined &&
    'credentials' in navigator &&
    navigator.credentials !== undefined
  );
}

/**
 * Check if platform authenticator (Touch ID, Face ID, Windows Hello) is available
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    const available =
      await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (error) {
    logger.error(
      'Failed to check platform authenticator availability',
      error as Error,
    );
    return false;
  }
}

/**
 * Get browser and platform information for device naming
 */
export function getDeviceInfo(): { browser: string; platform: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown Browser';
  let platform = 'Unknown Platform';

  // Detect browser
  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edge')) browser = 'Edge';

  // Detect platform
  if (ua.includes('Mac OS X')) platform = 'macOS';
  else if (ua.includes('Windows')) platform = 'Windows';
  else if (ua.includes('iPhone')) platform = 'iPhone';
  else if (ua.includes('iPad')) platform = 'iPad';
  else if (ua.includes('Android')) platform = 'Android';
  else if (ua.includes('Linux')) platform = 'Linux';

  return { browser, platform };
}

/**
 * Generate a friendly device name suggestion
 */
export function suggestDeviceName(): string {
  const { browser, platform } = getDeviceInfo();
  return `${platform} - ${browser}`;
}

// ============================================================================
// Credential Registration
// ============================================================================

/**
 * Register a new WebAuthn credential for encryption unlocking
 */
export async function registerWebAuthnCredential(
  options: WebAuthnRegistrationOptions,
): Promise<WebAuthnRegistrationResult> {

  if (!isWebAuthnSupported()) {
    throw new Error(
      'WebAuthn is not supported in your browser. Please use a modern browser like Chrome, Safari, Firefox, or Edge.',
    );
  }

  try {
    // Step 1: Request registration options from edge function
    const registrationInvokeEnvelope = getInvokeEnvelope(
      await supabase.functions.invoke('webauthn-register', {
        body: {
          deviceName: options.deviceName,
          deviceType: options.deviceType || 'platform',
        },
      }),
    );
    const registrationData = registrationInvokeEnvelope.data;
    const registrationError = registrationInvokeEnvelope.error;

    if (registrationError) {
      logger.error(
        'Failed to get registration options',
        {
          error:
            registrationError instanceof Error
              ? registrationError.message
              : String(registrationError),
          stack:
            registrationError instanceof Error
              ? registrationError.stack
              : undefined,
        },
        'registration',
      );
      throw new Error(
        `Registration failed: ${getUnknownMessage(registrationError, 'Unknown error')}`,
      );
    }

    if (!hasRegistrationOptions(registrationData)) {
      throw new Error('Invalid registration options received from server');
    }

    const registrationOptions: PublicKeyCredentialCreationOptionsJSON =
      registrationData.options;

    // Step 2: Prompt user for biometric authentication
    let registrationResponse: RegistrationResponseJSON;

    try {
      registrationResponse = await startRegistration({
        optionsJSON: registrationOptions,
      });
    } catch (error: unknown) {
      logger.error('User cancelled or failed biometric authentication', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      if (getUnknownName(error) === 'NotAllowedError') {
        throw new Error(
          'Biometric authentication was cancelled. Please try again and complete the authentication when prompted.',
        );
      }

      throw new Error(
        `Biometric authentication failed: ${getUnknownMessage(error, 'Unknown error')}`,
      );
    }

    // Step 3: Verify registration and store credential
    const masterKeyMaterial = getCachedMasterKeyMaterial(options.userId);

    if (!masterKeyMaterial) {
      throw new Error(
        'password not found: Encryption session not active. Please unlock your vault first.',
      );
    }

    const rawMasterKey = btoa(String.fromCharCode(...masterKeyMaterial));

    const verificationInvokeEnvelope = getInvokeEnvelope(
      await supabase.functions.invoke('webauthn-register-verify', {
        body: {
          registrationResponse,
          deviceName: options.deviceName,
          deviceType: options.deviceType || 'platform',
          rawMasterKey,
        },
      }),
    );
    const verificationData = verificationInvokeEnvelope.data;
    const verificationError = verificationInvokeEnvelope.error;

    if (verificationError) {
      logger.error(
        'Failed to verify registration',
        {
          error:
            verificationError instanceof Error
              ? verificationError.message
              : String(verificationError),
          stack:
            verificationError instanceof Error
              ? verificationError.stack
              : undefined,
        },
        'registration',
      );
      throw new Error(
        `Registration verification failed: ${getUnknownMessage(verificationError, 'Unknown error')}`,
      );
    }

    if (!hasRegistrationVerification(verificationData)) {
      throw new Error('Invalid verification response from server');
    }

    logger.info('WebAuthn credential registered successfully', {
      credentialId: verificationData.credential.credentialId,
    });

    return {
      credential: verificationData.credential,
      wrappedKey: verificationData.wrappedKey,
    };
  } catch (error) {
    logger.error('WebAuthn registration failed', error as Error);
    throw error;
  }
}

// ============================================================================
// Credential Authentication
// ============================================================================

/**
 * Authenticate using WebAuthn credential to unlock encryption (inline flow)
 */
export async function authenticateWithWebAuthn(
  options: AuthenticationOptions,
): Promise<AuthenticationResult> {

  if (!isWebAuthnSupported()) {
    return {
      success: false,
      error:
        'WebAuthn is not supported in your browser. Please use password unlock instead.',
    };
  }

  try {
    // Step 1: request authentication options from edge function
    const authInvokeEnvelope = getInvokeEnvelope(
      await supabase.functions.invoke('webauthn-authenticate', {
        body: {
          credentialId: options.credentialId,
        },
      }),
    );
    const data = authInvokeEnvelope.data;
    const error = authInvokeEnvelope.error;

    if (error) {
      logger.error(
        'Failed to get authentication options',
        {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
        'auth',
      );
      return {
        success: false,
        error: getUnknownMessage(
          error,
          'Unable to start biometric authentication. Please try again.',
        ),
      };
    }

    if (!hasAuthenticationOptions(data)) {
      return {
        success: false,
        error: 'Invalid authentication options received from server',
      };
    }

    // Step 2: prompt user for biometric authentication
    let authResponse: AuthenticationResponseJSON;
    try {
      authResponse = await startAuthentication({
        optionsJSON: data.options,
      });
    } catch (err: unknown) {
      if (getUnknownName(err) === 'NotAllowedError') {
        return {
          success: false,
          error: 'Biometric authentication was cancelled. Please try again.',
        };
      }

      logger.error(
        'WebAuthn authentication failed',
        {
          error: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        },
        'auth',
      );
      return {
        success: false,
        error: getUnknownMessage(
          err,
          'Biometric authentication failed. Please try again.',
        ),
      };
    }

    // Step 3: verify authentication and get wrapped key
    const verifyInvokeEnvelope = getInvokeEnvelope(
      await supabase.functions.invoke('webauthn-authenticate-verify', {
        body: {
          authenticationResponse: authResponse,
        },
      }),
    );
    const verifyData = verifyInvokeEnvelope.data;
    const verifyError = verifyInvokeEnvelope.error;

    if (verifyError) {
      logger.error(
        'Failed to verify authentication',
        {
          error:
            verifyError instanceof Error
              ? verifyError.message
              : String(verifyError),
          stack: verifyError instanceof Error ? verifyError.stack : undefined,
        },
        'auth',
      );
      return {
        success: false,
        error:
          getUnknownMessage(verifyError, '') ||
          'Biometric verification failed. Please try again.',
      };
    }

    if (!hasAuthenticationVerification(verifyData)) {
      return {
        success: false,
        error: 'Invalid authentication verification response from server',
      };
    }

    return {
      success: true,
      wrappedKey: verifyData.wrappedKey,
      credentialId:
        typeof verifyData.credentialId === 'string'
          ? verifyData.credentialId
          : undefined,
    };
  } catch (err) {
    logger.error(
      'Unexpected error during WebAuthn authentication',
      err as Error,
    );
    return {
      success: false,
      error:
        'Unexpected error during biometric authentication. Please try again.',
    };
  }
}

// ============================================================================
// Credential Management
// ============================================================================

/**
 * List all WebAuthn credentials for a user
 */
export async function listUserCredentials(
  userId: string,
): Promise<WebAuthnCredential[]> {

  try {
    const { data, error } = await supabase
      .from('webauthn_credentials')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to fetch credentials', error);
      throw new Error(`Failed to fetch credentials: ${error.message}`);
    }

    const credentials: WebAuthnCredential[] = (data || []).map((row) => ({
      id: row.id,
      credentialId: row.credential_id,
      deviceName: row.device_name || 'Unknown Device',
      deviceType: row.device_type as 'platform' | 'cross-platform',
      isActive: row.is_active,
      lastUsedAt: row.last_used_at,
      createdAt: row.created_at,
      backupEligible: row.backup_eligible || false,
      backupState: row.backup_state || false,
    }));

    return credentials;
  } catch (error) {
    logger.error('Failed to list credentials', error as Error);
    throw error;
  }
}

/**
 * Revoke (deactivate) a WebAuthn credential
 */
export async function revokeCredential(
  userId: string,
  credentialId: string,
): Promise<void> {

  try {
    const { error } = await supabase
      .from('webauthn_credentials')
      .update({ is_active: false })
      .eq('user_id', userId)
      .eq('credential_id', credentialId);

    if (error) {
      logger.error('Failed to revoke credential', error);
      throw new Error(`Failed to revoke credential: ${error.message}`);
    }

    logger.info('WebAuthn credential revoked', { credentialId });
  } catch (error) {
    logger.error('Failed to revoke credential', error as Error);
    throw error;
  }
}

/**
 * Update credential's last used timestamp
 */
export async function updateCredentialLastUsed(
  credentialId: string,
): Promise<void> {
  try {
    const { error } = await supabase
      .from('webauthn_credentials')
      .update({ last_used_at: new Date().toISOString() })
      .eq('credential_id', credentialId);

    if (error) {
      logger.error('Failed to update credential last used', error);
      // Don't throw - this is not critical
    }
  } catch (error) {
    logger.error('Failed to update credential last used', error as Error);
    // Don't throw - this is not critical
  }
}

/**
 * Check if user has any active WebAuthn credentials
 */
export async function hasWebAuthnCredentials(userId: string): Promise<boolean> {
  try {
    // Count active credentials. Some legacy rows may have is_active=null, treat those as active.
    const { count, error } = await supabase
      .from('webauthn_credentials')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .or('is_active.is.true,is_active.is.null')
      .limit(1);

    if (error) {
      logger.error('Failed to check credentials', {
        error,
        module: 'hasWebAuthnCredentials',
      });
      return false;
    }

    const hasCredentials = (count ?? 0) > 0;
    return hasCredentials;
  } catch (error) {
    logger.error('Failed to check credentials', error as Error);
    return false;
  }
}

// ============================================================================
// Encryption Key Wrapping
// ============================================================================

/**
 * Wrap encryption key with WebAuthn credential
 */
export async function wrapEncryptionKey(
  privateKeyPem: string,
  credentialId: string,
): Promise<WrappedKey> {

  try {
    const wrapInvokeEnvelope = getInvokeEnvelope(
      await supabase.functions.invoke('webauthn-wrap-key', {
        body: {
          privateKeyPem,
          credentialId,
        },
      }),
    );
    const data = wrapInvokeEnvelope.data;
    const error = wrapInvokeEnvelope.error;

    if (error) {
      logger.error(
        'Failed to wrap encryption key',
        {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
        'auth',
      );
      throw new Error(
        `Key wrapping failed: ${getUnknownMessage(error, 'Unknown error')}`,
      );
    }

    if (!hasWrappedKeyResponse(data)) {
      throw new Error('Invalid key wrapping response from server');
    }

    return data.wrappedKey;
  } catch (error) {
    logger.error(
      'Failed to wrap encryption key',
      { error: error instanceof Error ? error.message : String(error) },
      'auth',
    );
    throw error;
  }
}

/**
 * Unwrap encryption key using WebAuthn authentication
 */
export async function unwrapEncryptionKey(
  wrappedKey: WrappedKey,
  authenticationResponse: AuthenticationResponseJSON,
): Promise<string> {

  try {
    const unwrapInvokeEnvelope = getInvokeEnvelope(
      await supabase.functions.invoke('webauthn-unwrap-key', {
        body: {
          wrappedKey,
          authenticationResponse,
        },
      }),
    );
    const data = unwrapInvokeEnvelope.data;
    const error = unwrapInvokeEnvelope.error;

    if (error) {
      logger.error(
        'Failed to unwrap encryption key',
        {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
        'auth',
      );
      throw new Error(
        `Key unwrapping failed: ${getUnknownMessage(error, 'Unknown error')}`,
      );
    }

    if (!hasPrivateKeyResponse(data)) {
      throw new Error('Invalid key unwrapping response from server');
    }

    return data.privateKeyPem;
  } catch (error) {
    logger.error('Failed to unwrap encryption key', error as Error);
    throw error;
  }
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Format credential for display
 */
export function formatCredentialInfo(credential: WebAuthnCredential): string {
  const lastUsed = credential.lastUsedAt
    ? new Date(credential.lastUsedAt).toLocaleDateString()
    : 'Never';

  const icon = credential.deviceType === 'platform' ? '🔐' : '🔑';

  return `${icon} ${credential.deviceName} (Last used: ${lastUsed})`;
}
